import React, { useEffect, useState } from 'react';

// ==================== TYPES ====================
export interface AttendanceOverviewSummary {
  total_units: number;
  marked: number;
  partially_marked: number;
  unmarked: number;
  total_students: number;
  marked_students: number;
  attendance_percentage: number;
  present: number;
  absent: number;
  leave: number;
  half_day: number;
}

interface AttendanceBar {
  key: string;
  label: string;
  value: number;
  tone: 'blue' | 'red';
}

const ATTENDANCE_TONE_STYLES: Record<
  'blue' | 'red',
  { idleBg: string; idleBorder: string; activeGradient: string; glow: string; text: string }
> = {
  blue: {
    idleBg: '#bfdbfe',
    idleBorder: '#93c5fd',
    activeGradient: 'linear-gradient(180deg, #60a5fa 0%, #2563eb 100%)',
    glow: 'rgba(37, 99, 235, 0.35)',
    text: '#1e40af',
  },
  red: {
    idleBg: '#fecaca',
    idleBorder: '#fca5a5',
    activeGradient: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)',
    glow: 'rgba(220, 38, 38, 0.35)',
    text: '#dc2626',
  },
};

// ==================== COMPONENT ====================
// Present / Half Day / Leave / Absent, shown as 4 bars.
// Bars grow in from 0 on mount (staggered per bar) instead of just
// popping in at full height, and idle fills use solid pastel colors
// (not translucent) so they stay visible even before hover.
//
// NOTE: chart footprint (CHART_HEIGHT + paddings/margins) was previously
// ~110px tall, which made the "Attendance Today" stat card balloon far
// past the height of the plain chip-breakdown cards next to it. All the
// vertical spacing below is trimmed down so this chart's total height is
// roughly in line with a chip row instead.
const AttendanceSummaryChart: React.FC<{ summary: AttendanceOverviewSummary }> = ({ summary }) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, []);

  const bars: AttendanceBar[] = [
    { key: 'present', label: 'Present', value: summary.present, tone: 'blue' },
    { key: 'half_day', label: 'Half Day', value: summary.half_day, tone: 'blue' },
    { key: 'leave', label: 'Leave', value: summary.leave, tone: 'blue' },
    { key: 'absent', label: 'Absent', value: summary.absent, tone: 'red' },
  ];

  const maxValue = Math.max(...bars.map((b) => b.value), 1);
  const CHART_HEIGHT = 28;
  const MIN_BAR_HEIGHT = 3;

  return (
    <div style={{ marginTop: '4px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '6px',
          height: CHART_HEIGHT,
          padding: '16px 4px 0 4px',
          position: 'relative',
        }}
      >
        {bars.map((bar, index) => {
          const tone = ATTENDANCE_TONE_STYLES[bar.tone];
          const isHovered = hoveredKey === bar.key;
          const finalHeight = Math.max(
            MIN_BAR_HEIGHT,
            (bar.value / maxValue) * CHART_HEIGHT
          );
          const barHeight = animated ? finalHeight : 0;

          return (
            <div
              key={bar.key}
              style={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                justifyContent: 'flex-end',
              }}
              onMouseEnter={() => setHoveredKey(bar.key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              {isHovered && (
                <div
                  className="attendance-tooltip"
                  style={{
                    position: 'absolute',
                    bottom: `calc(${finalHeight}px + 8px)`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#ffffff',
                    borderRadius: '9px',
                    padding: '5px 10px',
                    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
                    border: `1px solid ${tone.idleBorder}`,
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                  }}
                >
                  <div style={{ fontSize: '0.58rem', fontWeight: 600, color: '#64748b' }}>
                    {bar.label}
                  </div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 800, color: tone.text }}>
                    {bar.value}
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderTop: '5px solid #ffffff',
                    }}
                  />
                </div>
              )}

              <div
                style={{
                  width: '100%',
                  maxWidth: 22,
                  height: `${barHeight}px`,
                  borderRadius: '6px 6px 3px 3px',
                  background: isHovered ? tone.activeGradient : tone.idleBg,
                  border: `1px solid ${tone.idleBorder}`,
                  boxShadow: isHovered ? `0 6px 14px ${tone.glow}` : 'none',
                  transition: `height 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.08}s, background 0.25s ease, box-shadow 0.25s ease`,
                  cursor: 'pointer',
                }}
              />
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '6px',
          marginTop: '4px',
        }}
      >
        {bars.map((bar) => (
          <div
            key={bar.key}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: '0.58rem',
              fontWeight: 600,
              color: hoveredKey === bar.key ? ATTENDANCE_TONE_STYLES[bar.tone].text : '#64748b',
              transition: 'color 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {bar.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttendanceSummaryChart;